import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCapability } from '../../../../hooks/use-capability';
import { usePresence } from '../../../../hooks/use-presence';
import { useAuthStore } from '../../../../stores/auth-store';
import { relativeTime } from '../../../conversations/format';
import { OverrideTypeBadge, PageHead } from '../../components';
import { useOverrides } from '../../hooks/use-overrides';
import {
  useApproveOverride,
  useApproveType1,
  useApproveType5,
  useApproveType6,
  useApproveType7,
  useApproveType8,
  useRejectOverride,
} from '../../hooks/use-overrides-mutations';
import { FilterChip } from '../inbox-time/FilterChip';

import { ApproveWithNoteModal } from './modals/ApproveWithNoteModal';
import { CapabilityOverrideModal } from './modals/CapabilityOverrideModal';
import { ForensicApprovalModal } from './modals/ForensicApprovalModal';
import { OverrideApprovalModal } from './modals/OverrideApprovalModal';
import { RejectOverrideModal } from './modals/RejectOverrideModal';
import { OverrideRequestCard } from './OverrideRequestCard';
import { OVERRIDE_TYPE_META, adaptOverrideCard, deriveUrgency } from './tela9-adapter';

import type { OverrideCardVM } from './tela9-adapter';
import type { OverrideRequestRaw, OverrideType } from '../../types';
import type { SupervisorVariant } from '../../view-models';
import type React from 'react';

const TYPE_OPTS: { value: OverrideType; label: string }[] = (
  Object.keys(OVERRIDE_TYPE_META) as OverrideType[]
).map((t) => ({ value: t, label: OVERRIDE_TYPE_META[t].label }));

function mapTier(pkg: string | undefined): SupervisorVariant {
  if (pkg === 'solo') return 'solo';
  if (pkg === 'regulated') return 'regulated';
  return 'team-pro';
}

function SoloEmpty(): React.JSX.Element {
  return (
    <>
      <PageHead title="Aprovação de Overrides" />
      <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
        <div style={{ fontSize: 48 }}>🔒</div>
        <h3 style={{ color: 'var(--fg-1)' }}>Solo não tem hierarquia</h3>
        <p>Overrides requerem Team-Pro+. Aprovação de overrides é um recurso de operação em equipe.</p>
      </div>
    </>
  );
}

type ModalKind = 'reject' | 'approve-note' | 'type-1' | 'type-5' | 'forensic';

/** Modal de aprovação por tipo de decisão (D2). direct + type-7 caem no approve-note. */
function approveKind(decision: OverrideCardVM['decision']): ModalKind {
  if (decision === 'type-1') return 'type-1';
  if (decision === 'type-5') return 'type-5';
  if (decision === 'forensic') return 'forensic';
  return 'approve-note';
}

const HISTORY_STATUS: Record<string, { label: string; pill: string }> = {
  approved: { label: 'aprovado', pill: 'resolved' },
  rejected: { label: 'rejeitado', pill: 'cancelled' },
  awaiting_dual_approval: { label: 'aguarda gestor', pill: 'in_progress' },
  expired: { label: 'expirado', pill: 'cancelled' },
  pending: { label: 'pendente', pill: 'in_progress' },
};

export function AprovacaoOverridesPage(): React.JSX.Element {
  const navigate = useNavigate();
  const variant = mapTier(useAuthStore((s) => s.tenant?.packageType));

  // Gate de acesso + gating POR AÇÃO (D2/D11). Cada AÇÃO é gated pela cap do seu tipo; o botão
  // "Aprovar" 2/3/4 usa `override-modo-operacao` (cap que o endpoint genérico checa). Hooks sempre
  // antes de early return e chamados individualmente (rules-of-hooks).
  const capByName: Record<string, boolean> = {
    'override-ai-required-validation': useCapability('override-ai-required-validation'),
    'override-carencia-proativa': useCapability('override-carencia-proativa'),
    'override-gatilho-protecao': useCapability('override-gatilho-protecao'),
    'override-modo-operacao': useCapability('override-modo-operacao'),
    'override-capability-temporaria': useCapability('override-capability-temporaria'),
    'override-legal-capacity-exception': useCapability('override-legal-capacity-exception'),
    'override-conflict-alert-accept': useCapability('override-conflict-alert-accept'),
    'override-h-c-adversarial-strict': useCapability('override-h-c-adversarial-strict'),
  };
  const hasAnyOverrideCap = Object.values(capByName).some(Boolean);
  const canReject = capByName['override-modo-operacao'] ?? false;

  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const [operatorFilter, setOperatorFilter] = useState<string | undefined>(undefined);
  const [active, setActive] = useState<{ vm: OverrideCardVM; kind: ModalKind } | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const pending = useOverrides('pending');
  const approved = useOverrides('approved');
  const rejected = useOverrides('rejected');
  // Banner honesto (D6): overrides que a supervisora aprovou e aguardam a 2ª aprovação do gestor.
  const awaitingDual = useOverrides('awaiting_dual_approval');
  const presence = usePresence();

  const mApprove = useApproveOverride();
  const mReject = useRejectOverride();
  const mType1 = useApproveType1();
  const mType5 = useApproveType5();
  const mType6 = useApproveType6();
  const mType7 = useApproveType7();
  const mType8 = useApproveType8();

  const nameFor = useMemo(() => {
    const list = presence.data as unknown as { userId: string; name?: string }[];
    return (userId: string): string | null => list.find((p) => p.userId === userId)?.name ?? null;
  }, [presence.data]);

  if (variant === 'solo' || !hasAnyOverrideCap) return <SoloEmpty />;

  const now = Date.now();
  const allPending: OverrideCardVM[] = pending.rows.map(adaptOverrideCard);

  // Filtros client-side (D10): Tipo (server-side disponível, aplicado aqui sobre a página) + Operador (sem nome no list).
  const operatorOpts = Array.from(new Set(allPending.map((c) => c.requesterUserId))).map((id) => ({
    value: id,
    label: nameFor(id) ?? `#${id}`,
  }));

  const filteredPending = allPending.filter((c) => {
    if (typeFilter !== undefined && c.type !== typeFilter) return false;
    if (operatorFilter !== undefined && c.requesterUserId !== operatorFilter) return false;
    return true;
  });

  const urgentCount = filteredPending.filter((c) => deriveUrgency(c.expiresAt, new Date(now))).length;

  const history: OverrideRequestRaw[] = [...approved.rows, ...rejected.rows].sort((a, b) =>
    (b.approvedAt ?? b.updatedAt).localeCompare(a.approvedAt ?? a.updatedAt),
  );

  const seeContext = (vm: OverrideCardVM): void => {
    if (vm.conversationId !== null) navigate(`/supervisor/acompanhamento/${vm.conversationId}`);
  };

  const openModal = (vm: OverrideCardVM, kind: ModalKind): void => {
    setOutcome(null);
    setFailed(false);
    setActive({ vm, kind });
  };
  const closeModal = (): void => {
    setActive(null);
  };

  const anyPending =
    mApprove.isPending ||
    mReject.isPending ||
    mType1.isPending ||
    mType5.isPending ||
    mType6.isPending ||
    mType7.isPending ||
    mType8.isPending;

  // Desfecho honesto: dupla aprovação vem da RESPOSTA (dualApprovalRequestId), nunca hardcoded.
  const onApproveSuccess = (res: unknown): void => {
    if (
      res !== null &&
      typeof res === 'object' &&
      'dualApprovalRequestId' in res &&
      (res as { dualApprovalRequestId: string | null }).dualApprovalRequestId !== null
    ) {
      setOutcome('Enviado ao gestor para 2ª aprovação.');
    } else {
      closeModal();
    }
  };
  const onFail = (): void => {
    setFailed(true);
  };

  const confirmDirect = (note: string | undefined): void => {
    if (active === null) return;
    mApprove.mutate(
      { id: active.vm.id, ...(note !== undefined ? { approvalNote: note } : {}) },
      { onSuccess: () => closeModal(), onError: onFail },
    );
  };
  const confirmType7 = (note: string | undefined): void => {
    if (active === null) return;
    mType7.mutate(
      { id: active.vm.id, ...(note !== undefined ? { approvalNote: note } : {}) },
      { onSuccess: onApproveSuccess, onError: onFail },
    );
  };
  const confirmReject = (note: string): void => {
    if (active === null) return;
    mReject.mutate(
      { id: active.vm.id, approvalNote: note },
      { onSuccess: () => closeModal(), onError: onFail },
    );
  };
  const confirmType1 = (args: { editedText: string; approvalNote?: string }): void => {
    if (active === null) return;
    mType1.mutate({ id: active.vm.id, ...args }, { onSuccess: () => closeModal(), onError: onFail });
  };
  const confirmType5 = (args: { effectiveUntil: string; approvalNote?: string }): void => {
    if (active === null) return;
    mType5.mutate({ id: active.vm.id, ...args }, { onSuccess: () => closeModal(), onError: onFail });
  };
  const confirmForensic = (args: {
    documentationDocumentIds: string[];
    approvalNote?: string;
  }): void => {
    if (active === null) return;
    const mutation = active.vm.type === 'legal_capacity_exception' ? mType6 : mType8;
    mutation.mutate(
      { id: active.vm.id, ...args },
      { onSuccess: onApproveSuccess, onError: onFail },
    );
  };

  return (
    <>
      {awaitingDual.rows.length > 0 && (
        <div className="banner banner--info">
          <span className="banner__icon" aria-hidden="true">
            ⏳
          </span>
          <div className="banner__body">
            <span className="banner__title">
              {awaitingDual.rows.length} override
              {awaitingDual.rows.length === 1 ? '' : 's'} aguarda
              {awaitingDual.rows.length === 1 ? '' : 'm'} aprovação dupla
            </span>
            <div className="banner__sub">Aprovado por você · aguardando gestor</div>
          </div>
        </div>
      )}

      <PageHead
        title="Aprovação de Overrides"
        subtitle={
          filteredPending.length === 0
            ? 'Fila vazia · equipe alinhada com validações'
            : `${filteredPending.length} pendentes · ${urgentCount} urgente${urgentCount === 1 ? '' : 's'}`
        }
        filters={
          <>
            <FilterChip
              label="Tipo"
              options={TYPE_OPTS}
              value={typeFilter}
              onChange={setTypeFilter}
            />
            <FilterChip
              label="Operador"
              options={operatorOpts}
              value={operatorFilter}
              onChange={setOperatorFilter}
            />
            <button type="button" className="chip chip--active" disabled>
              Status: Pendentes
            </button>
          </>
        }
      />

      {filteredPending.length === 0 && (
        <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
          <div style={{ fontSize: 48 }}>☕</div>
          <h3 style={{ color: 'var(--fg-1)' }}>Nenhum override pendente</h3>
          <p>Equipe está alinhada com validações.</p>
        </div>
      )}

      {filteredPending.length > 0 && (
        <div className="tela9-body">
          <div className="sla-section-title" style={{ marginBottom: 0 }}>
            Pendentes ({filteredPending.length})
          </div>
          {filteredPending.map((vm) => (
            <OverrideRequestCard
              key={vm.id}
              vm={vm}
              requesterName={nameFor(vm.requesterUserId)}
              urgent={deriveUrgency(vm.expiresAt, new Date(now))}
              onSeeContext={seeContext}
              canApprove={capByName[vm.approveCap] ?? false}
              canReject={canReject}
              onApprove={(v) => openModal(v, approveKind(v.decision))}
              onReject={(v) => openModal(v, 'reject')}
            />
          ))}

          {history.length > 0 && (
            <>
              <div className="sla-section-title" style={{ marginTop: 'var(--space-4)' }}>
                Aprovados/Rejeitados ({history.length})
              </div>
              <div className="ti-group">
                <div className="ti-group__body">
                  {history.map((h) => {
                    const meta = HISTORY_STATUS[h.status] ?? {
                      label: h.status,
                      pill: 'in_progress',
                    };
                    const when = h.approvedAt ?? h.updatedAt;
                    return (
                      <div key={h.id} className="pend-team-card">
                        <div className="pend-team-card__head">
                          <OverrideTypeBadge type={h.overrideType} />
                          <span style={{ flex: 1 }} />
                          <span className={`status-pill status-pill--${meta.pill}`}>{meta.label}</span>
                        </div>
                        <div className="pend-team-card__meta">
                          <span>{nameFor(h.requesterUserId) ?? `#${h.requesterUserId}`}</span>
                          <span className="pend-team-card__sep" aria-hidden="true" />
                          <span>{relativeTime(when)}</span>
                          {h.approvalNote !== null && h.approvalNote !== '' && (
                            <>
                              <span className="pend-team-card__sep" aria-hidden="true" />
                              <span className="tweaks-hint">&quot;{h.approvalNote}&quot;</span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {active?.kind === 'reject' && (
        <RejectOverrideModal
          open
          typeLabel={active.vm.typeLabel}
          isPending={anyPending}
          failed={failed}
          outcome={outcome}
          onClose={closeModal}
          onConfirm={confirmReject}
        />
      )}
      {active?.kind === 'approve-note' && (
        <ApproveWithNoteModal
          open
          typeLabel={active.vm.typeLabel}
          dualApproval={active.vm.dualApproval}
          isPending={anyPending}
          failed={failed}
          outcome={outcome}
          onClose={closeModal}
          onConfirm={active.vm.decision === 'type-7' ? confirmType7 : confirmDirect}
        />
      )}
      {active?.kind === 'type-1' && (
        <OverrideApprovalModal
          open
          payload={active.vm.payload}
          isPending={anyPending}
          failed={failed}
          outcome={outcome}
          onClose={closeModal}
          onConfirm={confirmType1}
        />
      )}
      {active?.kind === 'type-5' && (
        <CapabilityOverrideModal
          open
          isPending={anyPending}
          failed={failed}
          outcome={outcome}
          onClose={closeModal}
          onConfirm={confirmType5}
        />
      )}
      {active?.kind === 'forensic' && (
        <ForensicApprovalModal
          open
          dualApproval={active.vm.dualApproval}
          bondId={active.vm.bondId ?? undefined}
          isPending={anyPending}
          failed={failed}
          outcome={outcome}
          onClose={closeModal}
          onConfirm={confirmForensic}
        />
      )}
    </>
  );
}
